import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { adminFaqApi } from '@/components/AdminPanel/Faq/api/adminFaqApi';
import { adminFaqPageTypeApi } from '@/components/AdminPanel/Faq/api/adminFaqPageTypeApi';
import { faqPageTypeMapperFromResponse } from '@/components/AdminPanel/Faq/helpers/faqPageTypeMapper';
import type {
  TFaqPageType,
  TFaqPageTypeRequest
} from '@/components/AdminPanel/Faq/types/faqPageType.types';
import { faqApi } from '@/components/Faq/api/faqApi';
import { faqMapperFromResponse } from '@/components/Shared/widgets/faq-main-page/helpers/faqMapper';
import type {
  TFaq,
  TFaqSection
} from '@/components/Shared/widgets/faq-main-page/types/faq.types';

import { createAsyncThunk } from './create-async-thunk';

interface TFaqStore {
  adminFaqArticle: TFaq | null;
  adminFaqList: TFaq[];
  faqArticle: TFaq | null;
  faqList: TFaq[];
  faqPageType: TFaqPageType | null;
  faqPageTypes: TFaqPageType[];
  faqPageTypesTotal: number;
  faqSections: TFaqSection[];
  isLoadingAdminFaqList: boolean;
  isLoadingFaqArticle: boolean;
  isLoadingFaqList: boolean;
  isLoadingFaqPageTypes: boolean;
}

const initialState: TFaqStore = {
  adminFaqArticle: null,
  adminFaqList: [],
  faqArticle: null,
  faqList: [],
  faqPageType: null,
  faqPageTypes: [],
  faqPageTypesTotal: 0,
  faqSections: [],
  isLoadingAdminFaqList: false,
  isLoadingFaqArticle: false,
  isLoadingFaqList: false,
  isLoadingFaqPageTypes: false
};

export const getAdminFaqList = createAsyncThunk(
  'faq/getAdminFaqList',
  async () => {
    return await adminFaqApi.getAllFaq();
  }
);

export const getAdminFaqArticle = createAsyncThunk(
  'faq/getAdminFaqArticle',
  async (id: string) => {
    return await adminFaqApi.getFaqArticle(id);
  }
);

export const getFaqList = createAsyncThunk('faq/getFaqList', async () => {
  return await faqApi.getAllFaq();
});

export const getFaqArticle = createAsyncThunk(
  'faq/getFaqArticle',
  async (id: string) => {
    return await faqApi.getFaqArticle(id);
  }
);

export const getAdminFaqPageTypes = createAsyncThunk(
  'faq/getAdminFaqPageTypes',
  async () => {
    return await adminFaqPageTypeApi.getAllFaqPageTypes();
  }
);

export const getAdminFaqPageType = createAsyncThunk(
  'faq/getAdminFaqPageType',
  async (id: string) => {
    return await adminFaqPageTypeApi.getFaqPageType(id);
  }
);

export const createAdminFaqPageType = createAsyncThunk(
  'faq/createAdminFaqPageType',
  async (body: TFaqPageTypeRequest) => {
    return await adminFaqPageTypeApi.createFaqPageType(body);
  }
);

export const updateAdminFaqPageType = createAsyncThunk(
  'faq/updateAdminFaqPageType',
  async (body: TFaqPageTypeRequest) => {
    return await adminFaqPageTypeApi.updateFaqPageType(body);
  }
);

export const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {
    resetAdminFaqArticle: (state) => {
      state.adminFaqArticle = null;
    },
    resetFaqArticle: (state) => {
      state.faqArticle = null;
    },
    resetAdminFaqPageType: (state) => {
      state.faqPageType = null;
    },
    setFaqSections: (state, action: PayloadAction<TFaqSection[]>) => {
      state.faqSections = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminFaqList.pending, (state) => {
        state.isLoadingAdminFaqList = true;
      })
      .addCase(getAdminFaqList.fulfilled, (state, action) => {
        state.adminFaqList = action.payload.map(faqMapperFromResponse);
        state.isLoadingAdminFaqList = false;
      })
      .addCase(getAdminFaqList.rejected, (state) => {
        state.isLoadingAdminFaqList = false;
      })
      .addCase(getAdminFaqArticle.fulfilled, (state, action) => {
        state.adminFaqArticle = faqMapperFromResponse(action.payload);
      })
      .addCase(getFaqList.pending, (state) => {
        state.isLoadingFaqList = true;
      })
      .addCase(getFaqList.fulfilled, (state, action) => {
        state.faqList = action.payload.map(faqMapperFromResponse);
        state.isLoadingFaqList = false;
      })
      .addCase(getFaqList.rejected, (state) => {
        state.isLoadingFaqList = false;
      })
      .addCase(getFaqArticle.pending, (state) => {
        state.isLoadingFaqArticle = true;
      })
      .addCase(getFaqArticle.fulfilled, (state, action) => {
        state.faqArticle = faqMapperFromResponse(action.payload);
        state.isLoadingFaqArticle = false;
      })
      .addCase(getFaqArticle.rejected, (state) => {
        state.isLoadingFaqArticle = false;
      })
      .addCase(getAdminFaqPageTypes.pending, (state) => {
        state.isLoadingFaqPageTypes = true;
      })
      .addCase(getAdminFaqPageTypes.fulfilled, (state, action) => {
        state.faqPageTypes = action.payload
          .map(faqPageTypeMapperFromResponse)
          .sort(
            (a, b) =>
              Number(a.position || 0) - Number(b.position || 0) ||
              a.name.localeCompare(b.name)
          );
        state.faqPageTypesTotal = action.payload.length;
        state.isLoadingFaqPageTypes = false;
      })
      .addCase(getAdminFaqPageTypes.rejected, (state) => {
        state.isLoadingFaqPageTypes = false;
      })
      .addCase(getAdminFaqPageType.fulfilled, (state, action) => {
        state.faqPageType = faqPageTypeMapperFromResponse(action.payload);
      });
  }
});

export const {
  resetAdminFaqArticle,
  resetFaqArticle,
  resetAdminFaqPageType,
  setFaqSections
} = faqSlice.actions;
